import React, { useState, useEffect } from 'react';

import './App.css';
import {
  RESEARCHES,
  ResearchId,
  ITEM_TYPES,
  ITEMS,
  ItemType,
} from './constants';
import { DebugState } from './hooks/useDebugState';
import {
  getCurrentTick,
  enableDebugStepMode,
  disableDebugStepMode,
  stepAndPause,
  isInDebugStepMode,
} from './hooks/useGameTick';
import { useLogger, LoggerType, LogLevel } from './Logger';
import { type SoundSettings } from './services/SoundEngine';
import {
  loadDebugConfirmationState,
  saveDebugConfirmationState,
  DebugConfirmationState,
} from './utils/DebugMenuStorage';

// Game state interface for debug menu
interface GameStateForDebug {
  addItems: (itemType: ItemType, amount: number) => number;
  hasEnabledDebug: boolean;
  resetResearch: (researchId: ResearchId) => void;
  resetAllResearch: () => void;
  prestigePoints: number;
  prestigeCount: number;
  forcePrestige: (count: number) => void;
}

// DebugMenu props interface
interface DebugMenuProps {
  debugMenuOpen: boolean;
  setDebugMenuOpen: (open: boolean) => void;
  gameState: GameStateForDebug;
  debugState: DebugState;
  setDebugState: React.Dispatch<React.SetStateAction<DebugState>>;
  onEnableDebugMode: () => void;
  soundSettings: SoundSettings;
  setSoundSettings: (
    value: SoundSettings | ((prev: SoundSettings) => SoundSettings)
  ) => void;
  resetSoundSettings: () => void;
}

const DebugMenu: React.FC<DebugMenuProps> = ({
  debugMenuOpen: _debugMenuOpen,
  setDebugMenuOpen,
  gameState,
  debugState,
  setDebugState,
  onEnableDebugMode,
  soundSettings,
  setSoundSettings,
  resetSoundSettings,
}) => {
  const [selectedItem, setSelectedItem] = useState<ItemType>('iron-plate');
  const [itemAmount, setItemAmount] = useState<number>(100);
  const [prestigeAmount, setPrestigeAmount] = useState<number>(1);
  const [currentTick, setCurrentTick] = useState<number>(0);
  const [debugStepModeActive, setDebugStepModeActive] =
    useState<boolean>(false);
  const [confirmationState, setConfirmationState] =
    useState<DebugConfirmationState>(() => loadDebugConfirmationState());
  const loggerHook = useLogger();

  // Update tick count and debug step mode status
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTick(getCurrentTick());
      setDebugStepModeActive(isInDebugStepMode());
    }, 100); // Update every 100ms

    return () => clearInterval(interval);
  }, []);

  const handleAddItems = (): void => {
    if (itemAmount <= 0) {
      return;
    }

    gameState.addItems(selectedItem, itemAmount);
  };

  const handleResetResearch = (researchId: ResearchId): void => {
    gameState.resetResearch(researchId);
  };

  const handleResetAllResearch = (): void => {
    gameState.resetAllResearch();
  };

  const handleForcePrestige = (): void => {
    const normalizedAmount = Number.isFinite(prestigeAmount)
      ? Math.floor(prestigeAmount)
      : 0;

    if (normalizedAmount <= 0) {
      return;
    }

    setPrestigeAmount(normalizedAmount);
    gameState.forcePrestige(normalizedAmount);
  };

  const handleToggleCollision = (): void => {
    setDebugState(prev => ({
      ...prev,
      collisionDisabled: !prev.collisionDisabled,
    }));
  };

  const handleToggleSpeed = (): void => {
    setDebugState(prev => ({
      ...prev,
      speedBoost: !prev.speedBoost,
    }));
  };

  const handleStepAndPause = (): void => {
    stepAndPause();
  };

  const handleToggleDebugStepMode = (): void => {
    if (debugStepModeActive) {
      disableDebugStepMode();
    } else {
      enableDebugStepMode();
    }
  };

  const handleResetDebug = (): void => {
    setDebugState({
      collisionDisabled: false,
      speedBoost: false,
      showDebugDetails: false,
      ignoreBuildingCosts: false,
      ignorePowerCosts: false,
    });
  };

  const handleConfirmEnableDebug = (): void => {
    const newCount = confirmationState.confirmationCount + 1;
    const newConfirmationState: DebugConfirmationState = {
      confirmationCount: newCount,
      isConfirmed: newCount >= 5,
    };

    setConfirmationState(newConfirmationState);
    saveDebugConfirmationState(newConfirmationState);

    if (newConfirmationState.isConfirmed) {
      onEnableDebugMode();
    }
  };

  const handleClose = (): void => {
    setDebugMenuOpen(false);
  };

  // If debug mode hasn't been enabled yet, show confirmation dialog
  if (!gameState.hasEnabledDebug && !confirmationState.isConfirmed) {
    return (
      <div className='debug-menu'>
        <div className='debug-menu-header'>
          <h3>⚠️ Enable Debug Mode</h3>
          <p>
            Activating debug mode will disable achievements for this save file
          </p>
        </div>

        <div className='debug-section'>
          <h4>⚠️ Warning: Debug Mode Activation</h4>
          <div className='debug-controls'>
            <p>
              Enabling debug mode will activate developer cheats and debugging
              tools.
              <br />
              <strong>
                This will permanently disable achievements for this save file.
              </strong>
            </p>

            <p>
              Click the button below{' '}
              <strong>
                {5 - confirmationState.confirmationCount} more times
              </strong>{' '}
              to confirm you want to enable debug mode.
            </p>

            <button
              onClick={handleConfirmEnableDebug}
              className='debug-button'
              style={{
                backgroundColor: '#ff6b6b',
                border: '2px solid #ff5252',
                fontWeight: 'bold',
              }}
            >
              {confirmationState.confirmationCount === 0
                ? 'Enable Debug Mode (Click 5 times to confirm)'
                : `Confirm Enable Debug Mode (${confirmationState.confirmationCount}/5)`}
            </button>

            {confirmationState.confirmationCount > 0 && (
              <p
                style={{
                  color: '#ff9800',
                  fontWeight: 'bold',
                  marginTop: '10px',
                }}
              >
                Progress: {confirmationState.confirmationCount}/5 confirmations
              </p>
            )}
          </div>
        </div>

        <div className='debug-section'>
          <h4>Actions</h4>
          <div className='debug-controls'>
            <button onClick={handleClose} className='debug-button close'>
              Cancel & Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Show the actual debug menu once confirmed
  return (
    <div className='debug-menu'>
      <div className='debug-menu-header'>
        <h3>🐛 Debug Menu</h3>
        <p>Developer cheats and debugging tools</p>
        {gameState.hasEnabledDebug && (
          <p style={{ color: '#ff9800', fontWeight: 'bold' }}>
            ⚠️ Achievements disabled for this save file
          </p>
        )}
      </div>

      <div className='debug-section'>
        <h4>Sound Engine Settings</h4>
        <div className='debug-controls'>
          <div className='resource-input'>
            <label htmlFor='debug-sfx-max-channels'>Max Channels / Type</label>
            <input
              id='debug-sfx-max-channels'
              type='number'
              min='1'
              max='12'
              value={soundSettings.maxChannelsPerType}
              onChange={e => {
                const nextValue = Number.parseInt(e.target.value, 10);
                setSoundSettings(prev => ({
                  ...prev,
                  maxChannelsPerType: Number.isFinite(nextValue)
                    ? nextValue
                    : prev.maxChannelsPerType,
                }));
              }}
            />
          </div>
          <div className='resource-input'>
            <label htmlFor='debug-sfx-max-distance'>Max Distance</label>
            <input
              id='debug-sfx-max-distance'
              type='number'
              min='1'
              max='128'
              value={soundSettings.maxDistance}
              onChange={e => {
                const nextValue = Number.parseInt(e.target.value, 10);
                setSoundSettings(prev => ({
                  ...prev,
                  maxDistance: Number.isFinite(nextValue)
                    ? nextValue
                    : prev.maxDistance,
                }));
              }}
            />
          </div>
          <div className='resource-input'>
            <label htmlFor='debug-sfx-falloff-start'>
              Volume Falloff Start
            </label>
            <input
              id='debug-sfx-falloff-start'
              type='number'
              min='0'
              max='128'
              value={soundSettings.volumeFalloffStart}
              onChange={e => {
                const nextValue = Number.parseInt(e.target.value, 10);
                setSoundSettings(prev => ({
                  ...prev,
                  volumeFalloffStart: Number.isFinite(nextValue)
                    ? nextValue
                    : prev.volumeFalloffStart,
                }));
              }}
            />
          </div>
        </div>

        <div className='debug-controls'>
          <button
            onClick={resetSoundSettings}
            className='debug-button reset'
            type='button'
          >
            Reset Sound Settings
          </button>
          <p>
            Adjust how many factories can emit audio and how quickly the sounds
            fade with distance.
          </p>
        </div>
      </div>

      <div className='debug-section'>
        <h4>Inventory Cheats</h4>
        <div className='debug-controls'>
          <div className='resource-input'>
            <label>Item:</label>
            <select
              value={selectedItem}
              onChange={e => setSelectedItem(e.target.value as ItemType)}
            >
              {ITEM_TYPES.map(itemType => {
                const itemConfig = Object.values(ITEMS).find(
                  config => config.type === itemType
                );
                return (
                  <option key={itemType} value={itemType}>
                    {itemConfig?.name ?? itemType}
                  </option>
                );
              })}
            </select>
          </div>
          <div className='resource-input'>
            <label>Amount:</label>
            <input
              type='number'
              value={itemAmount}
              onChange={e => setItemAmount(parseInt(e.target.value) || 0)}
              min='1'
              max='10000'
            />
          </div>
          <button onClick={handleAddItems} className='debug-button'>
            Add Items
          </button>
        </div>
      </div>

      <div className='debug-section'>
        <h4>Research Tools</h4>
        <div className='debug-controls debug-research-controls'>
          <button
            onClick={handleResetAllResearch}
            className='debug-button reset'
            title='Lock all research and clear progress'
          >
            Reset All Research
          </button>

          <div className='debug-research-list'>
            {Object.values(RESEARCHES).map(research => (
              <button
                key={research.id}
                onClick={() => handleResetResearch(research.id)}
                className='debug-button'
                title={`Reset progress for ${research.name}`}
              >
                Reset {research.name}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className='debug-section'>
        <h4>Prestige Tools</h4>
        <div className='debug-controls'>
          <div className='resource-input'>
            <label>Prestige Count:</label>
            <input
              type='number'
              min='1'
              value={prestigeAmount}
              onChange={e =>
                setPrestigeAmount(parseInt(e.target.value, 10) || 0)
              }
            />
          </div>
          <button
            onClick={handleForcePrestige}
            className='debug-button'
            title='Grant prestige and perform a full reset'
          >
            Add Prestige &amp; Reset
          </button>
        </div>
        <p>
          Current Prestige: <strong>{gameState.prestigePoints}</strong> points •
          Runs: <strong>{gameState.prestigeCount}</strong>
        </p>
        <p>
          Forces a full prestige reset and awards the specified amount of
          prestige points and launches.
        </p>
      </div>

      <div className='debug-section'>
        <h4>Movement Cheats</h4>
        <div className='debug-controls'>
          <label className='debug-checkbox'>
            <input
              type='checkbox'
              checked={debugState.collisionDisabled}
              onChange={handleToggleCollision}
            />
            Disable Collision
          </label>
          <label className='debug-checkbox'>
            <input
              type='checkbox'
              checked={debugState.speedBoost}
              onChange={handleToggleSpeed}
            />
            Speed Boost (5x faster)
          </label>
        </div>
      </div>

      <div className='debug-section'>
        <h4>Economy Cheats</h4>
        <div className='debug-controls'>
          <label className='debug-checkbox'>
            <input
              type='checkbox'
              checked={debugState.ignoreBuildingCosts}
              onChange={() =>
                setDebugState(prev => ({
                  ...prev,
                  ignoreBuildingCosts: !prev.ignoreBuildingCosts,
                }))
              }
            />
            Ignore building material costs
          </label>
          <label className='debug-checkbox'>
            <input
              type='checkbox'
              checked={debugState.ignorePowerCosts}
              onChange={() =>
                setDebugState(prev => ({
                  ...prev,
                  ignorePowerCosts: !prev.ignorePowerCosts,
                }))
              }
            />
            Ignore electricity costs (unlimited power)
          </label>
        </div>
      </div>

      <div className='debug-section'>
        <h4>Display Options</h4>
        <div className='debug-controls'>
          <label className='debug-checkbox'>
            <input
              type='checkbox'
              checked={debugState.showDebugDetails}
              onChange={() =>
                setDebugState(prev => ({
                  ...prev,
                  showDebugDetails: !prev.showDebugDetails,
                }))
              }
            />
            Show debug details for target square
          </label>
        </div>
      </div>

      <div className='debug-section'>
        <h4>Tick Step Debugging</h4>
        <div className='debug-controls'>
          <div className='tick-info'>
            <p>
              Current Tick: <strong>{currentTick}</strong>
            </p>
            <p>
              Mode:{' '}
              <strong>
                {debugStepModeActive ? '🐛 Debug Step Mode' : '▶️ Normal Speed'}
              </strong>
            </p>
          </div>
          <div className='step-controls'>
            <button
              onClick={handleStepAndPause}
              className='debug-button'
              title='Advance exactly 1 tick and pause'
            >
              {debugStepModeActive ? 'Step Forward ⏭️' : 'Step & Pause ⏭️'}
            </button>
            <button
              onClick={handleToggleDebugStepMode}
              className={`debug-button ${debugStepModeActive ? 'active' : ''}`}
              title='Toggle between normal speed and step debugging mode'
            >
              {debugStepModeActive ? 'Resume Normal ▶️' : 'Enable Step Mode ⏸️'}
            </button>
          </div>
        </div>
      </div>

      <div className='debug-section'>
        <h4>Logging System</h4>
        <div className='debug-controls'>
          <div className='logger-controls'>
            <h5>Logger Types</h5>
            <div className='logger-checkboxes'>
              {Object.values(LoggerType).map(type => (
                <label key={type} className='debug-checkbox'>
                  <input
                    type='checkbox'
                    checked={loggerHook.isLoggerEnabled(type)}
                    onChange={e => {
                      if (e.target.checked) {
                        loggerHook.enableLogger(type);
                      } else {
                        loggerHook.disableLogger(type);
                      }
                    }}
                  />
                  {type
                    .replace('-', ' ')
                    .replace(/\b\w/g, l => l.toUpperCase())}
                </label>
              ))}
            </div>
          </div>
          <div className='log-level-controls'>
            <h5>Log Level</h5>
            <select
              value={loggerHook.logLevel}
              onChange={e =>
                loggerHook.setLogLevel(parseInt(e.target.value) as LogLevel)
              }
              className='debug-select'
            >
              <option value={LogLevel.DEBUG}>Debug</option>
              <option value={LogLevel.INFO}>Info</option>
              <option value={LogLevel.WARN}>Warning</option>
              <option value={LogLevel.ERROR}>Error</option>
            </select>
          </div>
          <div className='log-actions'>
            <button
              onClick={() => loggerHook.clearHistory()}
              className='debug-button'
            >
              Clear Log History
            </button>
            <button
              onClick={() => {
                const stats = loggerHook.getLogStats();
                // eslint-disable-next-line no-console
                console.table(stats);
              }}
              className='debug-button'
            >
              Show Log Stats
            </button>
          </div>
        </div>
      </div>

      <div className='debug-section'>
        <h4>Debug Actions</h4>
        <div className='debug-controls'>
          <button onClick={handleResetDebug} className='debug-button reset'>
            Reset All Debug Settings
          </button>
          <button onClick={handleClose} className='debug-button close'>
            Close Debug Menu
          </button>
        </div>
      </div>

      <div className='debug-info'>
        <p>Logger controls above affect console output</p>
        <p>
          <kbd>ESC</kbd> - Close menu
        </p>
      </div>
    </div>
  );
};

export default DebugMenu;
